import {
  Button,
  Card,
  EmptyState,
  FormField,
  Icon,
  Input,
  KeyValue,
  List,
  ListRow,
  MoreMenu,
  PageHeader,
  phoneQuery,
  Section,
  Skeleton,
  Text,
  Textarea,
  useMediaQuery,
  useToast,
  type MenuItem,
} from '@ui';
import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage, formErrors } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import {
  invoicePdfUrl,
  useInvoice,
  useInvoiceAction,
  useSale,
  useSaleAction,
  type Invoice,
} from '../api';
import { InvoiceRecipientDialog } from '../components/InvoiceRecipientDialog';
import { PaymentDialog } from '../components/PaymentDialog';
import { InvoiceBadge } from '../components/SaleBadges';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

type Open = 'finalize' | 'delete' | 'cancel' | 'send' | 'note' | 'pay' | 'recipient' | null;

/** One invoice: PDF, finalising, sending, payment, cancellation (INVC-001 – INVC-010). */
export function InvoiceDetailPage() {
  const { t } = useTranslation();
  const { invoiceId = '' } = useParams();
  const invoice = useInvoice(invoiceId);
  if (invoice.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(invoice.error)}
        actions={<BackLink to="/verkauf/rechnungen" label={t('sales.tabs.invoices')} />}
      />
    );
  }
  if (!invoice.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={8} />
      </div>
    );
  }
  return <InvoiceDetail invoice={invoice.data} />;
}

/** Starts the download of the invoice PDF. */
function download(url: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  a.click();
}

function InvoiceDetail({ invoice }: { invoice: Invoice }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const phone = useMediaQuery(phoneQuery);
  const [open, setOpen] = useState<Open>(null);
  const [to, setTo] = useState(invoice.customer_email ?? '');
  const [note, setNote] = useState(invoice.note ?? '');
  const action = useInvoiceAction();
  const saleAction = useSaleAction();
  const close = () => {
    action.reset();
    setOpen(null);
  };
  const write = can('invoices.write');
  const draft = invoice.status === 'draft';
  const final = invoice.status === 'final';
  const original = invoice.kind === 'invoice';
  const content = invoice.content;
  // Draft of an anonymous sale: the recipient was given for this invoice only.
  const freeRecipient = draft && !invoice.customer;
  // The amount decides whether the recipient may stay empty (§ 33 UStDV);
  // only needed to change the recipient of such a draft.
  const sale = useSale(invoice.sale_id, write && freeRecipient);
  const pdf = invoicePdfUrl(invoice.id, invoice.version);
  const run = (a: Parameters<typeof action.mutate>[0], message: string, after?: () => void) =>
    action.mutate(a, {
      onSuccess: () => {
        toast.show(message);
        close();
        after?.();
      },
    });
  const sendErrors = formErrors(action.error, { to: ['email.', 'invoice.email_missing'] });

  // One primary action, depending on where the invoice stands.
  const primary: 'finalize' | 'pay' | 'send' | null = !write
    ? null
    : draft
      ? 'finalize'
      : final && original && !invoice.paid
        ? 'pay'
        : final
          ? 'send'
          : null;

  const more: MenuItem[] = [
    ...(write && final && primary !== 'send'
      ? [{ key: 'send', label: t('sales.invoice.send'), onSelect: () => setOpen('send') }]
      : []),
    { key: 'pdf', label: t('sales.invoice.download'), onSelect: () => download(pdf) },
    ...(write && draft
      ? [
          { key: 'note', label: t('sales.invoice.editNote'), onSelect: () => setOpen('note') },
          ...(freeRecipient
            ? [
                {
                  key: 'recipient',
                  label: t('sales.invoice.editRecipient'),
                  onSelect: () => setOpen('recipient'),
                },
              ]
            : []),
          {
            key: 'delete',
            label: t('sales.invoice.delete'),
            tone: 'danger' as const,
            separated: true,
            onSelect: () => setOpen('delete'),
          },
        ]
      : []),
    ...(write && final && original
      ? [
          {
            key: 'cancel',
            label: t('sales.invoice.cancel'),
            tone: 'danger' as const,
            separated: true,
            onSelect: () => setOpen('cancel'),
          },
        ]
      : []),
  ];

  const meta: ReactNode[] = [
    ...(invoice.customer ? [invoice.customer.name] : []),
    ...(content ? [formatEuro(content.total_cents)] : []),
    <InvoiceBadge
      key="status"
      status={invoice.status}
      paid={invoice.paid}
      overdue={invoice.overdue}
      cancellation={!original}
    />,
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        back={<BackLink to="/verkauf/rechnungen" label={t('sales.tabs.invoices')} />}
        title={invoice.number ?? t('sales.invoice.draftTitle')}
        meta={meta.map((m, i) => (
          <Fragment key={i}>
            {i > 0 && ' · '}
            {m}
          </Fragment>
        ))}
        actions={
          <>
            {primary === 'finalize' && (
              <Button onClick={() => setOpen('finalize')}>{t('sales.invoice.finalize')}</Button>
            )}
            {primary === 'pay' && (
              <Button onClick={() => setOpen('pay')}>{t('sales.detail.pay')}</Button>
            )}
            {primary === 'send' && (
              <Button onClick={() => setOpen('send')}>{t('sales.invoice.send')}</Button>
            )}
            <MoreMenu label={t('common.moreActions')} items={more} />
          </>
        }
      />

      <div className={styles.columns}>
        {phone ? (
          // Phones show embedded PDFs poorly or not at all (iOS Safari, #60).
          <List>
            <ListRow
              title={
                <a href={pdf} target="_blank" rel="noopener">
                  {t('sales.invoice.openPdf')}
                </a>
              }
              trailing={<Icon name="external-link" size="sm" />}
            />
          </List>
        ) : (
          <Card padding="sm" aria-label={t('sales.invoice.preview')}>
            <iframe
              className={styles.pdf}
              src={`${pdf}#navpanes=0&view=FitH`}
              title={t('sales.invoice.preview')}
            />
          </Card>
        )}
        <div className={styles.stack}>
          <Section title={t('sales.invoice.details')}>
            <KeyValue
              items={[
                {
                  key: 'customer',
                  label: t('sales.columns.customer'),
                  value: invoice.customer && (
                    <Link to={`/kunden/${invoice.customer.id}`}>{invoice.customer.name}</Link>
                  ),
                },
                {
                  key: 'recipient',
                  label: t('sales.invoice.recipient'),
                  value: content ? (
                    <span className={styles.notes}>
                      {[content.recipient_name, ...content.recipient_lines].join('\n') ||
                        t('sales.invoice.cashSale')}
                    </span>
                  ) : freeRecipient ? (
                    <span className={styles.notes}>
                      {invoice.recipient
                        ? [invoice.recipient.name, ...invoice.recipient.lines]
                            .filter(Boolean)
                            .join('\n')
                        : t('sales.invoice.cashSale')}
                    </span>
                  ) : null,
                },
                {
                  key: 'issued',
                  label: t('sales.invoice.issueDate'),
                  value: content && formatDate(content.issue_date),
                },
                {
                  key: 'service',
                  label: t('sales.invoice.serviceDate'),
                  value: content && formatDate(content.service_date),
                },
                {
                  key: 'due',
                  label: t('sales.invoice.dueDate'),
                  value: content?.due_date && formatDate(content.due_date),
                },
                {
                  key: 'total',
                  label: t('sales.columns.total'),
                  value: content && (
                    <Text as="span" numeric>
                      {formatEuro(content.total_cents)}
                    </Text>
                  ),
                },
                {
                  key: 'sale',
                  label: t('sales.invoice.sale'),
                  value: (
                    <Link to={`/verkauf/${invoice.sale_id}`}>{t('sales.invoice.openSale')}</Link>
                  ),
                },
                {
                  key: 'original',
                  label: t('sales.invoice.original'),
                  value: invoice.original && (
                    <Link to={`/verkauf/rechnungen/${invoice.original.id}`}>
                      {invoice.original.number}
                    </Link>
                  ),
                },
                {
                  key: 'cancelledBy',
                  label: t('sales.invoice.cancelledBy'),
                  value: invoice.cancelled_by && (
                    <Link to={`/verkauf/rechnungen/${invoice.cancelled_by.id}`}>
                      {invoice.cancelled_by.number}
                    </Link>
                  ),
                },
                {
                  key: 'note',
                  label: t('sales.invoice.note'),
                  value: invoice.note && <span className={styles.notes}>{invoice.note}</span>,
                },
                {
                  key: 'finalized',
                  label: t('sales.invoice.finalizedAt'),
                  value: invoice.finalized_at && formatDateTime(invoice.finalized_at),
                },
              ]}
            />
          </Section>
          {!draft && (
            <Section title={t('sales.invoice.dispatches')}>
              <List>
                {invoice.dispatches.length === 0 ? (
                  <ListRow title={<Text tone="muted">{t('sales.invoice.notSent')}</Text>} />
                ) : (
                  invoice.dispatches.map((d) => (
                    <ListRow
                      key={d.sent_at}
                      title={d.to}
                      description={[formatDateTime(d.sent_at), d.by].filter(Boolean).join(' · ')}
                    />
                  ))
                )}
              </List>
            </Section>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={open === 'finalize'}
        onClose={close}
        tone="default"
        title={t('sales.invoice.finalizeTitle')}
        description={t('sales.invoice.finalizeText')}
        confirmLabel={t('sales.invoice.finalize')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() => run({ id: invoice.id, action: 'finalize' }, t('sales.invoice.finalized'))}
      />
      <ConfirmDialog
        open={open === 'delete'}
        onClose={close}
        title={t('sales.invoice.deleteTitle')}
        description={t('sales.invoice.deleteText')}
        confirmLabel={t('common.delete')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          run({ id: invoice.id, action: 'delete' }, t('sales.invoice.deleted'), () =>
            navigate(`/verkauf/${invoice.sale_id}`),
          )
        }
      />
      {/* Cancellation is irreversible (INVC-006), hence the confirmation. */}
      <ConfirmDialog
        open={open === 'cancel'}
        onClose={close}
        title={t('sales.invoice.cancelTitle', { number: invoice.number })}
        description={t('sales.invoice.cancelText')}
        confirmLabel={t('sales.invoice.cancel')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          action.mutate(
            { id: invoice.id, action: 'cancel' },
            {
              onSuccess: (storno) => {
                toast.show(t('sales.invoice.cancelled'));
                close();
                if (storno && 'id' in storno) void navigate(`/verkauf/rechnungen/${storno.id}`);
              },
            },
          )
        }
      />
      {open === 'pay' && (
        <PaymentDialog
          open
          onClose={close}
          pending={action.isPending}
          error={action.error}
          onSubmit={(method, on) =>
            action.mutate(
              { id: invoice.id, action: 'pay', method, on },
              {
                onSuccess: () => {
                  // The payment belongs to the sale (INVC-010), so undo resets it there.
                  toast.show(
                    t('sales.detail.paid'),
                    'success',
                    can('sales.write')
                      ? {
                          label: t('common.undo'),
                          onClick: () =>
                            saleAction.mutate(
                              { id: invoice.sale_id, action: 'unpay' },
                              { onError: (e) => toast.show(errorMessage(e), 'danger') },
                            ),
                        }
                      : undefined,
                  );
                  close();
                },
              },
            )
          }
        />
      )}
      <FormDialog
        open={open === 'send'}
        onClose={close}
        title={t('sales.invoice.sendTitle', { number: invoice.number })}
        submitLabel={t('sales.invoice.send')}
        pending={action.isPending}
        error={sendErrors.general}
        onSubmit={() =>
          run(
            { id: invoice.id, action: 'send', to: to.trim() || undefined },
            t('sales.invoice.sent', { to: to.trim() || invoice.customer_email }),
          )
        }
      >
        <FormField label={t('sales.invoice.to')} required error={sendErrors.field.to}>
          {(p) => <Input {...p} type="email" value={to} onChange={(e) => setTo(e.target.value)} />}
        </FormField>
      </FormDialog>
      {open === 'recipient' && sale.data && (
        <InvoiceRecipientDialog
          onClose={close}
          totalCents={sale.data.total_cents}
          pending={action.isPending}
          error={action.error}
          current={invoice.recipient ?? null}
          onSubmit={(r) =>
            run(
              {
                id: invoice.id,
                action: 'recipient',
                // An e-mail address set via API or MCP is kept.
                recipient: r.recipient
                  ? { ...r.recipient, email: invoice.recipient?.email ?? null }
                  : null,
              },
              t('common.saved'),
            )
          }
        />
      )}
      {open === 'recipient' && !sale.data && (
        // The dialog needs the amount of the sale: say so while it loads or failed.
        <FormDialog
          open
          onClose={close}
          title={t('sales.invoice.editRecipient')}
          submitLabel={t('sales.invoice.retryLoad')}
          pending={sale.isFetching}
          error={sale.isError ? t('sales.invoice.saleLoadFailed') : undefined}
          onSubmit={() => void sale.refetch()}
        >
          {!sale.isError && (
            <Text size="sm" tone="muted">
              {t('sales.invoice.saleLoading')}
            </Text>
          )}
        </FormDialog>
      )}
      <FormDialog
        open={open === 'note'}
        onClose={close}
        title={t('sales.invoice.editNote')}
        submitLabel={t('common.save')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onSubmit={() =>
          run({ id: invoice.id, action: 'note', note: note.trim() || null }, t('common.saved'))
        }
      >
        <FormField label={t('sales.invoice.note')}>
          {(p) => (
            <Textarea {...p} rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          )}
        </FormField>
      </FormDialog>
    </div>
  );
}
